import { useCallback, useRef, useState } from 'react'
import { checkSignUpEmail } from 'api/auth'
import { trackClientEvent } from 'analytics/analytics'

interface SignUpEmailCheck {
  /**
   * Checks a new user's email once per submit and calls `onValid` when sign-up may continue to the
   * Join form. Returns a cleanup for the caller's effect, which drops the answer if the customer has
   * left the step by the time it arrives.
   */
  startEmailCheck: (user: App.User, onValid: () => void) => (() => void) | undefined
  /**
   * True while a check runs. Callers disable their submit meanwhile, so a double submit doesn't
   * send a second check.
   */
  isCheckingEmail: boolean
  /** svc-auth's message when the current submit's email was refused, e.g. "Invalid email." */
  rejectionMessage: string | undefined
}

/**
 * Checks a new customer's email when it is submitted, so an address svc-auth will refuse is fixed
 * where it was typed, before the Join form asks for name, phone and password. `submitCount` is the
 * caller's count of email submits.
 */
function useSignUpEmailCheck(submitCount: number): SignUpEmailCheck {
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  // Kept with its submit, so the next submit hides it straight away, wherever that email leads
  const [rejection, setRejection] = useState<{ submitCount: number, message: string }>()
  // The submit and user the last check was started for. The caller's effect also re-runs when a
  // social login toggles auth.processing; that must not check the email again. A new user entry for
  // the same submit (a repeated existence answer) is checked again rather than dropped.
  const lastEmailCheckRef = useRef<{ submitCount: number, user: App.User }>(undefined)

  const startEmailCheck = useCallback((user: App.User, onValid: () => void) => {
    const { email } = user
    const lastCheck = lastEmailCheckRef.current
    if (!email || (lastCheck?.submitCount === submitCount && lastCheck.user === user)) {
      return
    }
    lastEmailCheckRef.current = { submitCount, user }

    let cancelled = false
    setIsCheckingEmail(true)
    checkSignUpEmail(email).then((result) => {
      setIsCheckingEmail(false)
      trackClientEvent({
        subject: 'sign_up_email_check',
        action: result.outcome,
        category: 'logging',
        type: 'operational',
      })

      if (result.outcome === 'rejected') {
        setRejection({ submitCount, message: result.message })
      } else if (!cancelled) {
        onValid()
      }
    })
    return () => {
      cancelled = true
    }
  }, [submitCount])

  return {
    startEmailCheck,
    isCheckingEmail,
    rejectionMessage: rejection?.submitCount === submitCount ? rejection.message : undefined,
  }
}

export default useSignUpEmailCheck
